import type { NavSectionProps } from 'src/components/nav-section';

import { paths } from 'src/routes/paths';

import { CONFIG } from 'src/global-config';

import { SvgColor } from 'src/components/svg-color';

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor src={`${CONFIG.assetsDir}/assets/icons/navbar/${name}.svg`} />
);

const ICONS = {
  job: icon('ic-job'),
  blog: icon('ic-blog'),
  chat: icon('ic-chat'),
  mail: icon('ic-mail'),
  user: icon('ic-user'),
  file: icon('ic-file'),
  lock: icon('ic-lock'),
  tour: icon('ic-tour'),
  order: icon('ic-order'),
  label: icon('ic-label'),
  blank: icon('ic-blank'),
  kanban: icon('ic-kanban'),
  folder: icon('ic-folder'),
  course: icon('ic-course'),
  params: icon('ic-params'),
  banking: icon('ic-banking'),
  booking: icon('ic-booking'),
  invoice: icon('ic-invoice'),
  product: icon('ic-product'),
  calendar: icon('ic-calendar'),
  disabled: icon('ic-disabled'),
  external: icon('ic-external'),
  subpaths: icon('ic-subpaths'),
  menuItem: icon('ic-menu-item'),
  ecommerce: icon('ic-ecommerce'),
  analytics: icon('ic-analytics'),
  dashboard: icon('ic-dashboard'),
};

// ----------------------------------------------------------------------

/**
 * 1. 스튜디오 시스템
 */
const broadcastNav: NavSectionProps['data'][number] = {
  subheader: '스튜디오 시스템',
  items: [
    {
      title: '스튜디오 시스템',
      path: paths.broadcast.group.root,
      icon: ICONS.course,
      allowedRoles: '방송시스템',
      children: [
        /**
         * {@link ConnectionSettingsView}
         */
        {
          title: '연계 기관 관리',
          path: paths.broadcast.group.connection,
          allowedRoles: '방송시스템/방송 연계 설정',
        },
        /**
         * {@link LinkedScheduleCopyView}
         * {@link LinkedScheduleWeekView}
         */
        {
          title: '연계방송 편성',
          path: paths.broadcast.schedule.linked,
          allowedRoles: '방송시스템/연계방송 편성',
          deepMatch: true,
        },
        /**
         * {@link ContentsView}
         * {@link ContentsDetailView}
         */
        {
          title: '영상 콘텐츠 관리',
          path: paths.broadcast.group.contents,
          deepMatch: true,
          allowedRoles: '방송시스템/콘텐츠 관리',
        },
        /**
         * {@link ScheduleView}
         */
        {
          title: '편성표 관리',
          path: paths.broadcast.group.root,
          allowedRoles: '방송시스템/편성표 관리',
        },
        /**
         * {@link BroadcastMonitoringView}
         */
        {
          title: '실시간 방송 보기',
          path: paths.broadcast.group.monitoring,
          allowedRoles: '방송시스템/실시간 방송 모니터링',
        },
        /**
         * {@link CenterBroadcastView}
         * {@link CenterBroadcastDetailView}
         */
        {
          title: '경로당 모니터링',
          path: paths.broadcast.group.center_monitoring,
          deepMatch: true,
          allowedRoles: '방송시스템/경로당 모니터링',
        },
        /**
         * {@link BroadcastRecoveryView}
         */
        {
          title: '자동 복구 설정',
          path: paths.broadcast.group.auto_recovery,
          allowedRoles: '방송시스템/자동 복구 설정',
        },
      ],
    },
  ],
};

/**
 * 2. 양방향 화상시스템
 */
const interactiveNav: NavSectionProps['data'][number] = {
  subheader: '양방향 화상시스템',
  items: [
    {
      title: '양방향 화상시스템',
      path: paths.interactive.group.root,
      icon: ICONS.chat,
      allowedRoles: '양방향 화상시스템',
      children: [
        /**
         * {@link SettopView}
         */
        {
          title: '셋톱박스 기기 현황',
          path: paths.interactive.group.settop,
          allowedRoles: '양방향 화상시스템/셋톱박스 기기 현황',
        },
        /**
         * {@link SettopUsageListView}
         */
        {
          title: '이용량',
          path: paths.interactive.group.usage,
          allowedRoles: '양방향 화상시스템/이용량',
        },
      ],
    },
  ],
};

/**
 * 3. 헬스케어 시스템
 */
const healthcareNav: NavSectionProps['data'][number] = {
  subheader: '헬스케어 시스템',
  items: [
    {
      title: '헬스케어 시스템',
      path: paths.health.group.root,
      icon: ICONS.dashboard,
      allowedRoles: '헬스케어 시스템',
      children: [
        // { title: '헬스케어 기기 현황', path: paths.health.group.root },
        /**
         * {@link HealthcareDeviceUsageListView}
         */
        {
          title: '이용량',
          path: paths.health.group.usage,
          allowedRoles: '헬스케어 시스템/이용량',
        },
        /**
         * {@link HealthcareUsageLogListView}
         * {@link HealthcareUsageLogDetailView}
         */
        {
          title: '사용자 측정 이력',
          path: paths.health.group.usage_log,
          allowedRoles: '헬스케어 시스템/사용자 측정 이력',
        },
        /**
         * {@link PromotionalMaterialView}
         */
        {
          title: '시정 홍보자료 등록',
          path: paths.health.group.promotional_material,
          allowedRoles: '헬스케어 시스템/시정 홍보자료 등록',
        },
      ],
    },
  ],
};

/**
 * 4. 이용 현황 관리
 */
const usageManagementNav: NavSectionProps['data'][number] = {
  subheader: '이용 현황 관리',
  items: [
    {
      title: '이용 현황 관리',
      path: paths.usage_status.group.root,
      icon: ICONS.analytics,
      allowedRoles: '이용현황 관리',
      children: [
        /**
         * {@link UsageView}
         */
        {
          title: '경로당별 이용 현황',
          path: paths.usage_status.group.center,
          deepMatch: true,
          allowedRoles: '이용현황 관리/경로당별 이용현황',
        },
        /**
         * {@link UsagePerSystemView}
         */
        {
          title: '시스템별 이용 현황',
          path: paths.usage_status.group.system,
          allowedRoles: '이용현황 관리/시스템별 이용현황',
        },
        /**
         * {@link IssueUsageStatisticsView}
         */
        {
          title: '이슈사항 발생 통계',
          path: paths.usage_status.group.issue_stats,
          allowedRoles: '이용현황 관리/이슈사항 발생 통계',
        },
        /**
         * {@link LinkedBroadcastViewingView}
         */
        {
          title: '연계방송 시청현황',
          path: paths.usage_status.group.linked_broadcast_viewing,
          allowedRoles: '이용현황 관리/연계방송 시청현황',
        },
      ],
    },
  ],
};

/**
 * 5. 공지사항
 */
const noticeNav: NavSectionProps['data'][number] = {
  subheader: '공지사항',
  items: [
    {
      title: '공지사항',
      path: paths.notice.group.root,
      icon: ICONS.blog,
      allowedRoles: '공지사항',
      children: [
        /**
         * {@link NoticeSendView}
         */
        {
          title: '공지사항 발송',
          path: paths.notice.group.root,
          allowedRoles: '공지사항/공지사항 발송',
        },
        /**
         * {@link NoticeTemplateView}
         * {@link NoticeTemplateDetailView}
         */
        {
          title: '공지사항 템플릿',
          path: paths.notice.group.template,
          allowedRoles: '공지사항/공지사항 템플릿',
        },
        /**
         * {@link NoticeHistoryView}
         * {@link NoticeHistoryDetailView}
         */
        {
          title: '공지사항 발신 내역',
          path: paths.notice.group.history,
          deepMatch: true,
          allowedRoles: '공지사항/공지사항 발신 내역',
        },
        /**
         * {@link ReceiveNoticeView}
         */
        {
          title: '연계 공지사항 수신',
          path: paths.notice.group.receive_notice,
          allowedRoles: '공지사항/연계 공지사항 수신',
        },
      ],
    },
  ],
};

/**
 * 6. 서비스 운영 관리
 */
const serviceOperationNav: NavSectionProps['data'][number] = {
  subheader: '서비스 운영 관리',
  items: [
    {
      title: '서비스 운영 관리',
      path: paths.service_operation.group.root,
      icon: ICONS.job,
      allowedRoles: '서비스 운영 관리',
      children: [
        /**
         * {@link HelpdeskView}
         */
        {
          title: '헬프데스크 처리 현황',
          path: paths.service_operation.group.helpdesk,
          allowedRoles: '서비스 운영 관리/헬프데스크 처리 현황',
        },
        /**
         * {@link IssueManagementView}
         */
        {
          title: '이슈사항 관리',
          path: paths.service_operation.group.issue,
          allowedRoles: '서비스 운영 관리/이슈사항 관리',
        },
        /**
         * {@link ScenarioManagementView}
         */
        {
          title: '이슈사항 시나리오 관리',
          path: paths.service_operation.group.scenario,
          allowedRoles: '서비스 운영 관리/이슈사항 시나리오 관리',
        },
        /**
         * {@link IntegratedLogView}
         */
        {
          title: '통합 로그',
          path: paths.service_operation.group.integrated_log,
          allowedRoles: '서비스 운영 관리/통합 로그',
        },
        /**
         * {@link AccountLogView}
         */
        {
          title: '관리자 활동 내역',
          path: paths.service_operation.group.account_log,
          allowedRoles: '서비스 운영 관리/계정별 로그',
        },
        /**
         * {@link AccountManagementView}
         * {@link AccountManagementDetailView}
         */
        {
          title: '계정 관리',
          path: paths.service_operation.group.account,
          deepMatch: true,
          allowedRoles: '서비스 운영 관리/계정 관리',
        },
        // { title: '경로당 계정 관리', path: paths.service_operation.group.center_account },
      ],
    },
  ],
};

// ----------------------------------------------------------------------

export const navData: NavSectionProps['data'] = [
  broadcastNav,
  noticeNav,
  interactiveNav,
  healthcareNav,
  usageManagementNav,
  serviceOperationNav,
];
